import { useReducer, useCallback } from 'react'
import {
	AUDIO_DUCK_SOURCES,
	type AudioDuckSource,
	type ASLVideo,
	clamp,
} from '../utility'
import type { SpecialEffectId } from '@mission.io/mission-toolkit'

export type PlayerState = {
	isPlayerShaking: boolean,
	showDeathScreen: boolean,
	aslVideos: ASLVideo[],
	localSoundEffects: Array<{
		id: SpecialEffectId,
		fadeVolumePercentage?: number,
	}>,
	audioDucking: { [AudioDuckSource]: number },
}

export function setPlayerShaking(activate: boolean): SetPlayerShakingAction {
	return {
		type: 'SET_PLAYER_SHAKING',
		payload: activate,
	}
}

export function setShowDeathScreen(
	activate: boolean
): SetShowDeathScreenAction {
	return {
		type: 'SHOW_DEATH_SCREEN',
		payload: activate,
	}
}
export function addASLVideo(aslVideo: ASLVideo): AddASLVideoAction {
	return {
		type: 'ADD_ASL_VIDEO',
		payload: { aslVideo },
	}
}

export function removeASLVideo(id: string): RemoveASLVideoAction {
	return {
		type: 'REMOVE_ASL_VIDEO',
		payload: { id },
	}
}

export function resetASLVideos(): ResetASLVideosAction {
	return { type: 'RESET_ASL_VIDEOS' }
}

export function startLocalSoundEffect(
	id: SpecialEffectId
): StartLocalSoundEffectAction {
	return { type: 'START_LOCAL_SOUND_EFFECT', payload: { id } }
}

export function endLocalSoundEffect(
	id: SpecialEffectId
): EndLocalSoundEffectAction {
	return { type: 'END_LOCAL_SOUND_EFFECT', payload: { id } }
}

/**
 * Begin to fade out a local sound effect
 * @param {SpecialEffectId} id
 * @returns {UpdateLocalSoundEffectFadeAction}
 */
export function fadeOutLocalSoundEffect(
	id: SpecialEffectId
): UpdateLocalSoundEffectFadeAction {
	return {
		type: 'UPDATE_LOCAL_SOUND_EFFECT_FADE',
		payload: { id, fadeVolumePercentage: 1 },
	}
}

/**
 * Update the fade out value for a local sound effect
 * When the given fade out value (fadeVolumePercentage) is 0, the sound effect will be removed.
 * @param {SpecialEffectId} id
 * @param {number} fadeVolumePercentage
 * @returns {UpdateLocalSoundEffectFadeAction | EndLocalSoundEffectAction} An EndLocalSoundEffectAction if the fadeVolumePercentage is 0, otherwise an UpdateLocalSoundEffectFadeAction
 */
export function updateLocalSoundEffectFade(
	id: SpecialEffectId,
	fadeVolumePercentage: number
): UpdateLocalSoundEffectFadeAction | EndLocalSoundEffectAction {
	if (fadeVolumePercentage <= 0) {
		return {
			type: 'END_LOCAL_SOUND_EFFECT',
			payload: { id },
		}
	}
	return {
		type: 'UPDATE_LOCAL_SOUND_EFFECT_FADE',
		payload: { id, fadeVolumePercentage },
	}
}

export function updateAudioDuckingValue(
	amount: number | null,
	source: AudioDuckSource
): UpdateAudioDuckingValueAction {
	return {
		type: 'UPDATE_AUDIO_DUCKING_VALUE',
		payload: { value: amount, source },
	}
}

type SetPlayerShakingAction = {
	type: 'SET_PLAYER_SHAKING',
	payload: boolean,
}
type SetShowDeathScreenAction = {
	type: 'SHOW_DEATH_SCREEN',
	payload: boolean,
}

type AddASLVideoAction = {
	type: 'ADD_ASL_VIDEO',
	payload: { aslVideo: ASLVideo },
}

type RemoveASLVideoAction = {
	type: 'REMOVE_ASL_VIDEO',
	payload: { id: string },
}

type ResetASLVideosAction = { type: 'RESET_ASL_VIDEOS' }

type StartLocalSoundEffectAction = {
	type: 'START_LOCAL_SOUND_EFFECT',
	payload: { id: SpecialEffectId },
}

type EndLocalSoundEffectAction = {
	type: 'END_LOCAL_SOUND_EFFECT',
	payload: { id: SpecialEffectId },
}

type UpdateLocalSoundEffectFadeAction = {
	type: 'UPDATE_LOCAL_SOUND_EFFECT_FADE',
	payload: { id: SpecialEffectId, fadeVolumePercentage: number },
}

type UpdateAudioDuckingValueAction = {
	type: 'UPDATE_AUDIO_DUCKING_VALUE',
	payload: { value: number | null, source: AudioDuckSource },
}

export type Action =
	| SetPlayerShakingAction
	| SetShowDeathScreenAction
	| AddASLVideoAction
	| RemoveASLVideoAction
	| ResetASLVideosAction
	| StartLocalSoundEffectAction
	| EndLocalSoundEffectAction
	| UpdateLocalSoundEffectFadeAction
	| UpdateAudioDuckingValueAction

const initialState: PlayerState = {
	isPlayerShaking: false,
	showDeathScreen: false,
	aslVideos: [],
	localSoundEffects: [],
	audioDucking: {},
}

const NOT_SILENT_ACTION_TYPES = {
	START_LOCAL_SOUND_EFFECT: 'START_LOCAL_SOUND_EFFECT',
	END_LOCAL_SOUND_EFFECT: 'END_LOCAL_SOUND_EFFECT',
	FADE_OUT_LOCAL_SOUND_EFFECT: 'FADE_OUT_LOCAL_SOUND_EFFECT',
	UPDATE_AUDIO_DUCKING_VALUE: 'UPDATE_AUDIO_DUCKING_VALUE',
}

function reducer(state: PlayerState, action: Action) {
	switch (action.type) {
		case 'SET_PLAYER_SHAKING':
			return { ...state, isPlayerShaking: action.payload }
		case 'SHOW_DEATH_SCREEN':
			return { ...state, showDeathScreen: action.payload }
		case 'RESET_ASL_VIDEOS': {
			return { ...state, aslVideos: [] }
		}
		case 'ADD_ASL_VIDEO': {
			const { aslVideo } = action.payload
			if (state.aslVideos.find(video => video.id === aslVideo.id)) {
				// ATTEMPTED TO ADD AN ASL VIDEO THAT IS ALREADY QUEUED
				return state
			}
			return {
				...state,
				aslVideos: [...state.aslVideos, aslVideo],
			}
		}
		case 'REMOVE_ASL_VIDEO': {
			const { id } = action.payload
			const index = state.aslVideos.findIndex(aslVideo => aslVideo.id === id)
			if (index > -1) {
				return {
					...state,
					aslVideos: [
						...state.aslVideos.slice(0, index),
						...state.aslVideos.slice(index + 1),
					],
				}
			}
			return state
		}
		// CANNOT PLAY 2 OF THE SAME LOCAL SPECIAL EFFECTS AT THE SAME TIME
		case 'START_LOCAL_SOUND_EFFECT': {
			const { id } = action.payload
			if (state.localSoundEffects.find(({ id: _id }) => _id === id)) {
				return state
			}
			return {
				...state,
				localSoundEffects: [...state.localSoundEffects, { id }],
			}
		}
		case 'END_LOCAL_SOUND_EFFECT': {
			const { id } = action.payload
			const filteredSoundEffects = state.localSoundEffects.filter(
				localSoundEffect => localSoundEffect.id !== id
			)
			if (filteredSoundEffects.length === state.localSoundEffects.length) {
				return state
			}
			return {
				...state,
				localSoundEffects: filteredSoundEffects,
			}
		}
		case 'UPDATE_LOCAL_SOUND_EFFECT_FADE': {
			const { id, fadeVolumePercentage } = action.payload
			const soundEffectIndex = state.localSoundEffects.findIndex(
				localSoundEffect => localSoundEffect.id === id
			)
			if (soundEffectIndex === -1) {
				return state
			}

			return {
				...state,
				localSoundEffects: [
					...state.localSoundEffects.slice(0, soundEffectIndex),
					{ id, fadeVolumePercentage },
					...state.localSoundEffects.slice(soundEffectIndex + 1),
				],
			}
		}

		case 'UPDATE_AUDIO_DUCKING_VALUE': {
			const newDucking = { ...state.audioDucking }
			if (action.payload.value === null) {
				delete newDucking[action.payload.source]
			} else {
				newDucking[action.payload.source] = clamp(
					(newDucking[action.payload.source] || 0) + action.payload.value,
					0,
					AUDIO_DUCK_SOURCES[action.payload.source].PERCENTAGE
				)
			}
			return { ...state, audioDucking: newDucking }
		}
		default:
			throw new Error(
				`Action type ${action.type} is not allowed in player reducer`
			)
	}
}

export default function usePlayerReducer(
	isSilent: boolean
): [PlayerState, (Action) => void] {
	const [state, dispatch] = useReducer(reducer, initialState)
	const silentDispatch = useCallback(
		(action: Action) => {
			if (isSilent && NOT_SILENT_ACTION_TYPES.hasOwnProperty(action.type)) {
				return
			} else return dispatch(action)
		},
		[isSilent, dispatch]
	)
	return [state, silentDispatch]
}

/**
 * Returns whether or not the player is shaking
 * @param {PlayerState} state
 */
export function getIsShaking(state: PlayerState): boolean {
	return state.isPlayerShaking
}

/**
 * Returns whether or not to show death screen
 */
export function showDeathScreen(state: PlayerState): boolean {
	return state.showDeathScreen
}

/**
 * Returns the list of asl videos on queue
 * @param {PlayerState} state
 */
export function getASLVideos(state: PlayerState): Array<ASLVideo> {
	return state.aslVideos
}

/**
 * Returns the list of local effects (their ids and their volume percentages)
 * @param {PlayerState} state
 */
export function getLocalSoundEffects(
	state: PlayerState
): $PropertyType<PlayerState, 'localSoundEffects'> {
	return state.localSoundEffects
}

/**
 * Returns the audio ducking value in the player
 * @param {PlayerState} state
 */
export function getAudioDuckingValue(
	state: PlayerState,
	source: AudioDuckSource
): number {
	return state.audioDucking[source] || 0
}
